import React, { useState } from "react";
import { useCSVReader } from "react-papaparse";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { chunk } from "lodash";
import moment from "moment";

import { getVerificationStatusUploads, uploadVerificationStatus } from "services";

import { DEFAULT_SHORT_CACHE, GET_VERIFICATION_STATUS_UPLOADS, UPLOAD_VERIFICATION_STATUS } from "consts";

import { CSV, GetRootProps, VerificationStatusUpload, VerificationStatusUploadError, VerificationStatusUploadRow } from "models";

import { Button, Col, Icon, MainPage, ProgressBar, Row, Table, TagNormal, Typography, Message as message } from "components";

const chunkSize = 200;
const defaultLimit = 10;

const STATUS_COLORS: Record<string, string> = {
  processing: "blue",
  completed: "green",
  failed: "red",
  pending: "yellow",
};

export const VerificationStatus = () => {
  const [saving, setSaving] = useState(false);
  const [processedRowCount, setProcessedRowCount] = useState(0);
  const [totalRowCount, setTotalRowCount] = useState(0);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(defaultLimit);

  const { CSVReader } = useCSVReader();
  const queryClient = useQueryClient();

  const { mutateAsync } = useMutation({
    mutationKey: [UPLOAD_VERIFICATION_STATUS],
    mutationFn: uploadVerificationStatus,
  });

  const { data, isLoading } = useQuery({
    queryKey: [GET_VERIFICATION_STATUS_UPLOADS, page, limit],
    queryFn: () => getVerificationStatusUploads({ page, limit }),
    ...DEFAULT_SHORT_CACHE,
  });

  const processFile = async (csv: CSV) => {
    setValidationErrors([]);
    setSaving(true);

    const { data: csvData } = csv;
    const [headers, ...rows] = csvData;

    const practiceSlugIndex = headers.findIndex((header: string) => header === "practice_slug");
    const idIndex = headers.findIndex((header: string) => header === "id");
    const selectionTypeIndex = headers.findIndex((header: string) => header === "selection_type");

    if (practiceSlugIndex === -1 || idIndex === -1) {
      setSaving(false);
      setValidationErrors(["CSV must contain at least two columns: 'practice_slug' and 'id'."]);

      return;
    }

    const errors: string[] = [];
    const validRows: VerificationStatusUploadRow[] = [];

    rows.forEach((row: string[], index: number) => {
      const practiceSlug = row[practiceSlugIndex]?.trim();
      const id = row[idIndex]?.trim();

      if (!practiceSlug || !id) {
        errors.push(`Row ${index + 2}: missing ${!practiceSlug ? "practice_slug" : ""}${!practiceSlug && !id ? " and " : ""}${!id ? "id" : ""}`);

        return;
      }

      const selectionType = selectionTypeIndex !== -1 ? row[selectionTypeIndex]?.trim() || null : undefined;
      validRows.push({ practiceSlug, id, selectionType });
    });

    if (errors.length > 0) {
      setSaving(false);
      setValidationErrors(errors);

      return;
    }

    setTotalRowCount(validRows.length);

    const chunks = chunk(validRows, chunkSize);

    try {
      for (const c of chunks) {
        // eslint-disable-next-line no-await-in-loop
        await mutateAsync({ rows: c });
        setProcessedRowCount((count) => count + c.length);
      }

      message({
        content: `Successfully uploaded ${validRows.length} rows for verification status processing`,
        icon: "gremlin",
      });

      queryClient.invalidateQueries({ queryKey: [GET_VERIFICATION_STATUS_UPLOADS] });
    } catch {
      message({
        content: "Failed to upload verification status rows. Please try again.",
        icon: "gremlin",
      });
    }

    setProcessedRowCount(0);
    setTotalRowCount(0);
    setSaving(false);
  };

  const columns = [
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
    },
    {
      title: "Total Rows",
      dataIndex: "totalRows",
      key: "totalRows",
    },
    {
      title: "Failed Rows",
      dataIndex: "failedRows",
      key: "failedRows",
    },
    {
      title: "Admin User",
      dataIndex: "adminUser",
      key: "adminUser",
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
    },
  ];

  type TableRow = {
    key: string;
    expandable: boolean;
    errors: VerificationStatusUploadError[];
    status: React.ReactNode;
    totalRows: number;
    failedRows: number;
    adminUser: string;
    createdAt: string;
  };

  const tableData: TableRow[] =
    data?.uploads.map((upload: VerificationStatusUpload) => ({
      key: upload.id,
      expandable: upload.errors.length > 0,
      errors: upload.errors,
      status: <TagNormal text={upload.status} color={STATUS_COLORS[upload.status] ?? "grey-600"} />,
      totalRows: upload.totalRows,
      failedRows: upload.failedRows,
      adminUser: upload.adminUser ? `${upload.adminUser.firstName} ${upload.adminUser.lastName}` : "-",
      createdAt: moment(upload.createdAt).format("YYYY-MM-DD HH:mm"),
    })) ?? [];

  const handlePageChange = (newPage: number, pageSize: number) => {
    setPage(newPage);
    setLimit(pageSize);
  };

  return (
    <MainPage pageName="admin">
      <Row>
        <Col>
          <Typography variant="h1">Verification Status</Typography>
          <Typography variant="paragraph-m">
            Upload a CSV to bulk-set elements to a pending verification status. The CSV format should be as follows:
            <br />
            <Typography variant="paragraph-m" code>
              practice_slug,id,selection_type
            </Typography>
            <br />
            <Typography variant="paragraph-m" code>
              crops,8766e145-b3aa-4766-b3c2-92b6ed61f7c3,high yield
            </Typography>
            <br />
            The{" "}
            <Typography variant="paragraph-m" code>
              selection_type
            </Typography>{" "}
            column is optional (~2 words, e.g. &quot;high yield&quot;) and describes why the element was selected for verification.
          </Typography>
        </Col>
      </Row>
      <Row>
        <Col md={8}>
          <CSVReader
            config={{
              skipEmptyLines: true,
            }}
            noDrag
            style={{
              dropArea: {
                border: 0,
                padding: 0,
                margin: 0,
                width: "auto",
                height: "auto",
                display: "inline-block",
              },
              progressBar: {
                display: "none !important",
                opacity: 0,
              },
            }}
            onUploadAccepted={(v: CSV) => processFile(v)}
          >
            {({ getRootProps }: { getRootProps: GetRootProps }) => (
              <Button
                className="margin-left-50"
                disabled={saving}
                dataTestId="verification-status-upload-button"
                loading={saving}
                size="small"
                type="secondary"
                {...getRootProps()}
              >
                <Icon iconName="tabler/upload" size="s" />
              </Button>
            )}
          </CSVReader>

          {saving && (
            <>
              <Typography variant="paragraph-m" className="margin-left-300">
                {processedRowCount}/{totalRowCount} rows processed
              </Typography>

              <ProgressBar size="small" percent={Math.ceil((processedRowCount / totalRowCount) * 100)} />
            </>
          )}

          {validationErrors.length > 0 && (
            <div className="margin-top-200">
              {validationErrors.map((error) => (
                <Typography key={error} variant="paragraph-m" className="red-text">
                  {error}
                </Typography>
              ))}
            </div>
          )}
        </Col>
      </Row>

      <Row className="margin-top-400">
        <Col>
          <Typography variant="h2">Upload History</Typography>
          <Table<TableRow>
            rowKey="key"
            data={tableData}
            columns={columns}
            loading={isLoading}
            expandable={{
              showExpandColumn: true,
              expandIcon: ({ expanded, onExpand, record }) =>
                (record as TableRow).expandable ? (
                  <Icon
                    iconName={expanded ? "tabler/chevron-down" : "tabler/chevron-right"}
                    size="s"
                    className="pointer"
                    onClick={(e) => onExpand(record, e as unknown as React.MouseEvent<HTMLElement>)}
                  />
                ) : null,
              expandedRowRender: (record) => {
                const { errors } = record as TableRow;

                return (
                  <div>
                    {errors.map((error) => (
                      <>
                        <Typography key={`${error.row}-${error.id}`} variant="paragraph-m" className="red-text">
                          Row {error.row}: {error.message} (practice_slug: {error.practiceSlug}, id: {error.id})
                        </Typography>
                        <br />
                      </>
                    ))}
                  </div>
                );
              },
            }}
            pagination={{
              total: data?.count,
              defaultPageSize: defaultLimit,
              pageSize: limit,
              showSizeChanger: true,
              pageSizeOptions: [10, 20, 50, 100],
              onChange: handlePageChange,
            }}
          />
        </Col>
      </Row>
    </MainPage>
  );
};
